import axios from 'axios';
import { BASE_URL } from './api';

function authHeader(token, extra = {}) {
    return {
        'Authorization': `Token ${token}`,
        ...extra,
    };
}

function jsonHeader(token) {
    return authHeader(token, { 'Content-Type': 'application/json' });
}

export async function getInventoryItems(token, queryParams='') {
    return axios({
        method: 'get',
        url: `${BASE_URL}/inventory/${queryParams}`,
        headers: authHeader(token),
    });
}

export async function getInventoryCategories(token, queryParams='') {
    return axios({
        method: 'get',
        url: `${BASE_URL}/inventory_categories/${queryParams}`,
        headers: authHeader(token),
    });
}

// Legacy endpoints use the .then/.catch-and-return-error pattern that the
// existing modal callers expect (they check `result.status == 200`).
// New endpoints below let axios throw normally; consumers use try/catch.

export async function createInventoryItem(data, token) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/inventory/create_inventory_item/`,
        headers: jsonHeader(token),
        data,
    }).then(r => r).catch(e => { console.log(e); return e; });
}

export async function updateInventoryItemAndOwnership(itemId, data, token) {
    return axios({
        method: 'put',
        url: `${BASE_URL}/inventory/${itemId}/edit_inventory_item/`,
        headers: jsonHeader(token),
        data,
    }).then(r => r).catch(e => { console.log(e); return e; });
}

export async function updateInventoryItem(itemId, data, token) {
    return axios({
        method: 'put',
        url: `${BASE_URL}/inventory/${itemId}/`,
        headers: jsonHeader(token),
        data,
    }).then(r => r).catch(e => { console.log(e); return e; });
}

export async function deleteInventoryItem(itemId, token) {
    return axios({
        method: 'delete',
        url: `${BASE_URL}/inventory/${itemId}/`,
        headers: jsonHeader(token),
    }).then(r => r).catch(e => { console.log(e); return e; });
}

// PATCH partial update — used by inline edits in the grid (checkboxes,
// dblclick text edits). Lets the throw happen so callers can catch and
// surface the error.
export async function patchInventoryItem(itemId, data, token) {
    return axios({
        method: 'patch',
        url: `${BASE_URL}/inventory/${itemId}/`,
        headers: jsonHeader(token),
        data,
    });
}

// PATCH partial update on an InventoryItemUser row — used for the
// editable-after-the-fact assignment record (e.g. add condition_at_return
// hours later after talking to the holder).
export async function patchInventoryItemUser(rowId, data, token) {
    return axios({
        method: 'patch',
        url: `${BASE_URL}/inventory_items_users/${rowId}/`,
        headers: jsonHeader(token),
        data,
    });
}

export async function getInventoryItemsUsers(token, queryParams='') {
    return axios({
        method: 'get',
        url: `${BASE_URL}/inventory_items_users/${queryParams}`,
        headers: authHeader(token),
    });
}

// --- New endpoints for the inventory tracking v1 rebuild ---

export async function issueInventoryItem(itemId, data, token) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/inventory/${itemId}/issue_item/`,
        headers: jsonHeader(token),
        data,
    });
}

export async function recordInventoryReturn(itemId, data, token) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/inventory/${itemId}/record_return/`,
        headers: jsonHeader(token),
        data,
    });
}

export async function retireInventoryItem(itemId, data, token) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/inventory/${itemId}/retire/`,
        headers: jsonHeader(token),
        data,
    });
}

export async function unretireInventoryItem(itemId, token) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/inventory/${itemId}/unretire/`,
        headers: jsonHeader(token),
        data: {},
    });
}

export async function returnInventoryItemToStock(itemId, token) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/inventory/${itemId}/return_to_stock/`,
        headers: jsonHeader(token),
        data: {},
    });
}

export async function getInventoryByPerson(userId, token) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/inventory/by_person/?user_id=${userId}`,
        headers: authHeader(token),
    });
}

// Bulk variant — returns per-user reports for every pickable user in one
// round-trip. Used by the InventoryByPerson "All" view.
export async function getInventoryByAllPeople(token) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/inventory/by_all_people/`,
        headers: authHeader(token),
    });
}

// Assignment photos (S3-backed, multipart/form-data)
export async function uploadAssignmentPhoto({ assignmentId, phase, file, caption }, token) {
    const form = new FormData();
    form.append('inventory_item_user', assignmentId);
    form.append('phase', phase);
    form.append('file', file);
    if (caption) form.append('caption', caption);
    return axios({
        method: 'post',
        url: `${BASE_URL}/inventory_assignment_photos/`,
        headers: authHeader(token),
        data: form,
    });
}

export async function listAssignmentPhotos(token, queryParams='') {
    return axios({
        method: 'get',
        url: `${BASE_URL}/inventory_assignment_photos/${queryParams}`,
        headers: authHeader(token),
    });
}

export async function deleteAssignmentPhoto(photoId, token) {
    return axios({
        method: 'delete',
        url: `${BASE_URL}/inventory_assignment_photos/${photoId}/`,
        headers: authHeader(token),
    });
}

// Warranties
export async function listWarranties(token, queryParams='') {
    return axios({
        method: 'get',
        url: `${BASE_URL}/inventory_warranties/${queryParams}`,
        headers: authHeader(token),
    });
}

export async function getWarranty(warrantyId, token) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/inventory_warranties/${warrantyId}/`,
        headers: authHeader(token),
    });
}

export async function createWarranty(data, token) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/inventory_warranties/`,
        headers: jsonHeader(token),
        data,
    });
}

export async function updateWarranty(warrantyId, data, token) {
    return axios({
        method: 'patch',
        url: `${BASE_URL}/inventory_warranties/${warrantyId}/`,
        headers: jsonHeader(token),
        data,
    });
}

export async function deleteWarranty(warrantyId, token) {
    return axios({
        method: 'delete',
        url: `${BASE_URL}/inventory_warranties/${warrantyId}/`,
        headers: jsonHeader(token),
    });
}

export async function getWarrantyThresholdSettings(token) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/inventory_warranty_threshold_settings/current/`,
        headers: authHeader(token),
    });
}

export async function updateWarrantyThresholdSettings(data, token) {
    return axios({
        method: 'patch',
        url: `${BASE_URL}/inventory_warranty_threshold_settings/current/`,
        headers: jsonHeader(token),
        data,
    });
}

// Issues
export async function listInventoryIssues(token, queryParams='') {
    return axios({
        method: 'get',
        url: `${BASE_URL}/inventory_issues/${queryParams}`,
        headers: authHeader(token),
    });
}

export async function createInventoryIssue(data, token) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/inventory_issues/`,
        headers: jsonHeader(token),
        data,
    });
}

export async function updateInventoryIssue(issueId, data, token) {
    return axios({
        method: 'patch',
        url: `${BASE_URL}/inventory_issues/${issueId}/`,
        headers: jsonHeader(token),
        data,
    });
}

export async function deleteInventoryIssue(issueId, token) {
    return axios({
        method: 'delete',
        url: `${BASE_URL}/inventory_issues/${issueId}/`,
        headers: authHeader(token),
    });
}

export async function listIssueUpdates(token, issueId) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/inventory_issue_updates/?inventory_issue=${issueId}`,
        headers: authHeader(token),
    });
}

export async function createIssueUpdate(data, token) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/inventory_issue_updates/`,
        headers: jsonHeader(token),
        data,
    });
}

export async function uploadIssueAttachment({ issueId, updateId, file, caption }, token) {
    const form = new FormData();
    form.append('inventory_issue', issueId);
    if (updateId) form.append('inventory_issue_update', updateId);
    form.append('file', file);
    if (caption) form.append('caption', caption);
    return axios({
        method: 'post',
        url: `${BASE_URL}/inventory_issue_attachments/`,
        headers: authHeader(token),
        data: form,
    });
}
